import {
  assertEnvVar,
  createBasicFetcher,
  type Dataset,
  type ExportFormat,
  type Fetcher,
  H5GroveProvider,
  hasArrayShape,
  hasNumericType,
} from '@h5web/app';
import { type PropsWithChildren, useCallback, useRef } from 'react';
import { useSearchParams } from 'wouter';

import { useSessionId } from './icat';
import { assertDefinedSearchParam } from './utils';

const ICATPLUS_URL = import.meta.env.VITE_ICATPLUS_URL;
assertEnvVar(ICATPLUS_URL, 'VITE_ICATPLUS_URL');

const H5GROVE_URL = `${ICATPLUS_URL}/h5grove`;
const SUPPORTED_EXPORT_FORMATS = new Set<ExportFormat>(['npy', 'tiff']);

const basicFetcher = createBasicFetcher();

interface Props {}

function DataProvider(props: PropsWithChildren<Props>) {
  const { children } = props;

  const sessionId = useSessionId();

  /* The ICAT session ID changes often because the SSO ID token is refreshed regularly.
   * By storing it in a ref, we avoid reinstanciating the data provider every time
   * and refetching HDF5 data and metadata uncessarily. */
  const sessionIdRef = useRef<string>(sessionId);
  sessionIdRef.current = sessionId;

  const [searchParams] = useSearchParams();
  const name = searchParams.get('name') || ''; // optional as not all vis services require it
  const datafileId = searchParams.get('datafileId');
  assertDefinedSearchParam(datafileId, 'datafileId');

  // Patch fetcher to pass params expected by ICAT+
  const fetcher = useCallback(
    async (...args: Parameters<Fetcher>) => {
      const [url, params, opts] = args;
      const { file, ...otherParams } = params; // remove `file` (added by ICAT+)
      return basicFetcher(
        url,
        {
          sessionId: sessionIdRef.current,
          datafileId,
          ...otherParams,
        },
        opts,
      );
    },
    [sessionIdRef, datafileId],
  );

  // Take over creation of export URL for NPY/TIFF to pass params expected by ICAT+
  const getExportURL = useCallback(
    (format: ExportFormat, dataset: Dataset, selection?: string) => {
      if (
        !SUPPORTED_EXPORT_FORMATS.has(format) ||
        !hasArrayShape(dataset) ||
        !hasNumericType(dataset)
      ) {
        return undefined;
      }

      const params = new URLSearchParams({
        sessionId: sessionIdRef.current,
        datafileId,
        path: dataset.path,
        format,
        ...(selection && { selection }),
      });

      return new URL(`${H5GROVE_URL}/data/?${params.toString()}`);
    },
    [sessionIdRef, datafileId],
  );

  return (
    <H5GroveProvider
      url={H5GROVE_URL}
      filepath={name}
      fetcher={fetcher}
      getExportURL={getExportURL}
    >
      {children}
    </H5GroveProvider>
  );
}

export default DataProvider;
